import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatNumber } from "@/i18n/format";
import { getApiErrorMessage } from "@/lib/apiError";
import {
  type DealOutcomeStatus,
  formatOutcomeDate,
  lostReasonLabel,
  lostReasonOptions,
  outcomeLabel,
  outcomeVariant,
} from "@/lib/dealOutcome";
import type { DealLostReasonEnum, DealOutcomeSaveRequest, ModeratorDeal } from "@/services/moderators/generated/model";
import { useModeratorsDealsOutcomeSaveCreate } from "@/services/moderators/generated/moderators-deals/moderators-deals";

interface OutcomeForm {
  status: DealOutcomeStatus;
  reason: string;
  note: string;
  firstMeetingAt: string;
  proposalAt: string;
  contractSignedAt: string;
  lostAt: string;
  attributedToMatchpoint: boolean;
  dealValue: string;
  dealCurrency: string;
}

function toForm(deal: ModeratorDeal): OutcomeForm {
  const o = deal.outcome;
  return {
    status: o.status,
    reason: o.reason,
    note: o.note,
    firstMeetingAt: o.firstMeetingAt ?? "",
    proposalAt: o.proposalAt ?? "",
    contractSignedAt: o.contractSignedAt ?? "",
    lostAt: o.lostAt ?? "",
    attributedToMatchpoint: o.attributedToMatchpoint ?? !deal.isManual,
    dealValue: o.dealValue ?? "",
    dealCurrency: o.dealCurrency,
  };
}

/**
 * WP1.7 — moderator Outcome card: status, lost reason, stage dates (D1-7)
 * and success-fee attribution (D1-5). Companies only ever see the status
 * and stage dates.
 */
const DealOutcomeCard = ({ deal, onSaved }: { deal: ModeratorDeal; onSaved: () => void }) => {
  const { t } = useTranslation(["deals", "common"]);
  const [form, setForm] = useState<OutcomeForm>(() => toForm(deal));
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveMutation = useModeratorsDealsOutcomeSaveCreate();
  const o = deal.outcome;

  const set = <K extends keyof OutcomeForm>(key: K, value: OutcomeForm[K]) => setForm({ ...form, [key]: value });

  const submit = () => {
    if (form.status === "lost" && !form.reason) {
      setError(t("outcomeCard.errors.reasonRequired"));
      return;
    }
    if (form.dealValue && !form.dealCurrency.trim()) {
      setError(t("outcomeCard.errors.currencyRequired"));
      return;
    }
    const data: DealOutcomeSaveRequest = {
      status: form.status,
      note: form.note.trim(),
      firstMeetingAt: form.firstMeetingAt || null,
      proposalAt: form.proposalAt || null,
      contractSignedAt: form.contractSignedAt || null,
      lostAt: form.lostAt || null,
      dealValue: form.dealValue || null,
      dealCurrency: form.dealCurrency.trim(),
    };
    if (form.status === "lost") data.reason = form.reason as DealLostReasonEnum;
    if (form.status === "won") data.attributedToMatchpoint = form.attributedToMatchpoint;
    saveMutation.mutate(
      { id: deal.id, data },
      {
        onSuccess: () => {
          setError(null);
          setEditing(false);
          onSaved();
        },
        onError: (err) => setError(getApiErrorMessage(err, t("outcomeCard.errors.saveFailed"))),
      },
    );
  };

  return (
    <section className="mod-page__card">
      <div className="mod-page__card-header">
        <h2 className="mod-page__card-title">{t("outcomeCard.title")}</h2>
        {!editing ? (
          <button
            type="button"
            className="mod-page__btn mod-page__btn--primary"
            onClick={() => {
              setForm(toForm(deal));
              setError(null);
              setEditing(true);
            }}
          >
            {t("outcomeCard.edit")}
          </button>
        ) : null}
      </div>

      {!editing ? (
        <>
          <p className="mod-page__card-meta">
            <span className={`mod-page__chip mod-page__chip--${outcomeVariant(o.status)}`}>
              {outcomeLabel(o.status)}
            </span>
            {o.status === "lost" ? t("outcomeCard.reason", { reason: lostReasonLabel(o.reason) }) : null}
            {o.updatedAt ? t("outcomeCard.updated", { date: formatDateTime(o.updatedAt) }) : null}
          </p>
          {o.note ? <p className="mod-page__card-meta">{t("outcomeCard.internalNote", { note: o.note })}</p> : null}
          <p className="mod-page__card-meta">
            {t("outcomeCard.stages", {
              matchAccepted: formatOutcomeDate(o.matchAcceptedAt),
              firstMeeting: formatOutcomeDate(o.firstMeetingAt),
              proposal: formatOutcomeDate(o.proposalAt),
              contractSigned: formatOutcomeDate(o.contractSignedAt),
            })}
            {o.status === "lost" ? t("outcomeCard.lostOn", { date: formatOutcomeDate(o.lostAt) }) : null}
          </p>
          <p className="mod-page__card-meta">
            {t("outcomeCard.attributionLabel")}{" "}
            {o.status === "won"
              ? t(o.attributedToMatchpoint ? "outcomeCard.attributed" : "outcomeCard.notAttributed", {
                  date: formatOutcomeDate(o.wonAt),
                })
              : t("outcomeCard.attributionPending")}
            {o.dealValue
              ? t("outcomeCard.value", { value: formatNumber(o.dealValue), currency: o.dealCurrency })
              : null}
          </p>
          <p className="mod-page__card-meta">{t("outcomeCard.visibility")}</p>
        </>
      ) : (
        <div className="mod-modal__form">
          <div className="mod-modal__row">
            <label className="mod-modal__label">
              {t("outcomeCard.fields.status")}
              <select
                className="mod-page__select"
                value={form.status}
                onChange={(e) => set("status", e.target.value as DealOutcomeStatus)}
              >
                <option value="open">{outcomeLabel("open")}</option>
                <option value="won">{outcomeLabel("won")}</option>
                <option value="lost">{outcomeLabel("lost")}</option>
              </select>
            </label>
            {form.status === "lost" ? (
              <label className="mod-modal__label">
                {t("outcomeCard.fields.reasonRequired")}
                <select
                  className="mod-page__select"
                  value={form.reason}
                  onChange={(e) => set("reason", e.target.value)}
                >
                  <option value="">{t("outcomeCard.fields.selectReason")}</option>
                  {lostReasonOptions().map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>

          <div className="mod-modal__row">
            <label className="mod-modal__label">
              {t("outcomeCard.fields.firstMeeting")}
              <input
                type="date"
                className="mod-page__input"
                value={form.firstMeetingAt}
                onChange={(e) => set("firstMeetingAt", e.target.value)}
              />
            </label>
            <label className="mod-modal__label">
              {t("outcomeCard.fields.proposal")}
              <input
                type="date"
                className="mod-page__input"
                value={form.proposalAt}
                onChange={(e) => set("proposalAt", e.target.value)}
              />
            </label>
          </div>

          <div className="mod-modal__row">
            <label className="mod-modal__label">
              {t("outcomeCard.fields.contractSigned")}
              <input
                type="date"
                className="mod-page__input"
                value={form.contractSignedAt}
                onChange={(e) => set("contractSignedAt", e.target.value)}
              />
              <small>{t("outcomeCard.fields.contractSignedHint")}</small>
            </label>
            {form.status === "lost" ? (
              <label className="mod-modal__label">
                {t("outcomeCard.fields.lostOn")}
                <input
                  type="date"
                  className="mod-page__input"
                  value={form.lostAt}
                  onChange={(e) => set("lostAt", e.target.value)}
                />
                <small>{t("outcomeCard.fields.lostOnHint")}</small>
              </label>
            ) : null}
          </div>

          <label className="mod-modal__label">
            {form.status === "lost" && form.reason === "other"
              ? t("outcomeCard.fields.noteRequiredForOther")
              : t("outcomeCard.fields.noteOptional")}
            <textarea
              className="mod-page__input"
              rows={2}
              maxLength={2000}
              value={form.note}
              onChange={(e) => set("note", e.target.value)}
            />
            <small>{t("outcomeCard.fields.noteHint")}</small>
          </label>

          <div className="mod-modal__row">
            <label className="mod-modal__label">
              {t("outcomeCard.fields.dealValue")}
              <input
                type="number"
                step="0.01"
                min="0"
                className="mod-page__input"
                value={form.dealValue}
                onChange={(e) => set("dealValue", e.target.value)}
              />
            </label>
            <label className="mod-modal__label">
              {t("outcomeCard.fields.currency")}
              <input
                type="text"
                maxLength={3}
                placeholder="CAD" // i18n-ignore (ISO currency code)
                className="mod-page__input"
                value={form.dealCurrency}
                onChange={(e) => set("dealCurrency", e.target.value.toUpperCase())}
              />
            </label>
          </div>

          {form.status === "won" ? (
            <label className="mod-modal__label">
              <span>
                <input
                  type="checkbox"
                  checked={form.attributedToMatchpoint}
                  onChange={(e) => set("attributedToMatchpoint", e.target.checked)}
                />{" "}
                {t("outcomeCard.fields.attributed")}
              </span>
            </label>
          ) : null}

          {error ? <p className="mod-modal__error">{error}</p> : null}

          <div className="mod-modal__footer">
            <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={() => setEditing(false)}>
              {t("common:actions.cancel")}
            </button>
            <button
              type="button"
              className="mod-page__btn mod-page__btn--primary"
              disabled={saveMutation.isPending}
              onClick={submit}
            >
              {saveMutation.isPending ? t("common:actions.saving") : t("outcomeCard.save")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default DealOutcomeCard;
